<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ContactMultiSelectComboBox from 'dashboard/components-next/combobox/ContactMultiSelectComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { useStore } from 'dashboard/composables/store';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

// Variable placeholder helpers (to avoid Vue parser interpreting as variables)
const varPrimeiroNome = '{{primeiro_nome}}';
const varNome = '{{nome}}';
const varEmail = '{{email}}';
const varTelefone = '{{telefone}}';
const varEmpresa = '{{empresa}}';

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getEmailInboxes'),
  funnels: useMapGetter('funnels/getFunnels'),
  accountId: useMapGetter('getCurrentAccountId'),
};

const initialState = {
  title: '',
  inboxId: null,
  subject: '',
  messageContent: '',
  scheduledAt: null,
  audienceType: 'tags', // 'tags', 'funnel', 'contacts'
  selectedAudience: [],
  selectedContacts: [],
  selectedFunnel: null,
  selectedStage: null,
  // Advanced settings
  showAdvanced: false,
  // Rate limiting
  minDelaySeconds: 5,
  maxDelaySeconds: 15,
};

const state = reactive({ ...initialState });

// Form Errors
const formErrors = reactive({
  title: '',
  inbox: '',
  subject: '',
  message: '',
  audience: '',
  scheduledAt: '',
});

// Validation rules
const rules = {
  title: { required, minLength: minLength(3) },
  inboxId: { required },
  subject: { required, minLength: minLength(3) },
  messageContent: { required },
  scheduledAt: { required },
};

const v$ = useVuelidate(rules, state);

const isCreatingCampaign = computed(() => formState.uiFlags.value?.isCreating);

// Audience Type Options
const audienceTypeOptions = computed(() => [
  {
    value: 'tags',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE_TYPE.TAGS'),
  },
  {
    value: 'contacts',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE_TYPE.CONTACTS'),
  },
  {
    value: 'funnel',
    label: t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE_TYPE.FUNNEL'),
  },
]);

// Inbox Options
const inboxOptions = computed(() => {
  return (formState.inboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }));
});

// Audience List (Labels)
const audienceList = computed(() => {
  return (formState.labels.value || []).map(label => ({
    id: label.id,
    name: label.title,
    color: label.color,
  }));
});

// Funnel Options
const funnelOptions = computed(() => {
  return (formState.funnels.value || [])
    .filter(f => f.active)
    .map(funnel => ({
      value: funnel.id,
      label: `${funnel.settings?.icon || ''} ${funnel.name}`.trim(),
    }));
});

// Stage Options
const stageOptions = computed(() => {
  if (!state.selectedFunnel) return [];

  const funnel = (formState.funnels.value || []).find(
    f => f.id === state.selectedFunnel
  );
  if (!funnel || !funnel.stages) return [];

  return Object.entries(funnel.stages)
    .sort((a, b) => (a[1].position || 0) - (b[1].position || 0))
    .map(([key, stage]) => ({
      value: key,
      label: stage.label,
    }));
});

// Reset audience selections when audience type changes
watch(
  () => state.audienceType,
  () => {
    state.selectedAudience = [];
    state.selectedContacts = [];
    state.selectedFunnel = null;
    state.selectedStage = null;
  }
);

// Reset stage when funnel changes
watch(
  () => state.selectedFunnel,
  () => {
    state.selectedStage = null;
  }
);

// Load labels and funnels on mount
onMounted(async () => {
  await Promise.all([
    store.dispatch('labels/get'),
    store.dispatch('inboxes/get'),
    store.dispatch('funnels/get'),
  ]);
});

// Build audience array
const buildAudience = () => {
  if (state.audienceType === 'tags') {
    return state.selectedAudience.map(labelId => ({
      id: labelId,
      type: 'Label',
    }));
  }

  if (state.audienceType === 'contacts') {
    return state.selectedContacts.map(contactId => ({
      id: contactId,
      type: 'Contact',
    }));
  }

  if (
    state.audienceType === 'funnel' &&
    state.selectedFunnel &&
    state.selectedStage
  ) {
    return [
      {
        type: 'FunnelStage',
        funnel_id: state.selectedFunnel,
        stage_name: state.selectedStage,
      },
    ];
  }

  return [];
};

// Validate Form
const validateForm = async () => {
  const isValid = await v$.value.$validate();

  // Clear errors
  Object.keys(formErrors).forEach(key => {
    formErrors[key] = '';
  });

  if (!isValid) {
    if (v$.value.title.$error) {
      formErrors.title = t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.ERROR');
    }
    if (v$.value.inboxId.$error) {
      formErrors.inbox = t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.ERROR');
    }
    if (v$.value.subject.$error) {
      formErrors.subject = t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.ERROR');
    }
    if (v$.value.messageContent.$error) {
      formErrors.message = t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.ERROR');
    }
    if (v$.value.scheduledAt.$error) {
      formErrors.scheduledAt = t(
        'CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.ERROR'
      );
    }
  }

  // Validate audience
  const audience = buildAudience();
  if (audience.length === 0) {
    formErrors.audience = t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.ERROR');
    return false;
  }

  return isValid;
};

// Submit Form
const handleSubmit = async () => {
  const isValid = await validateForm();
  if (!isValid) return;

  const campaignDetails = {
    title: state.title,
    inbox_id: state.inboxId,
    message: state.messageContent,
    scheduled_at: new Date(state.scheduledAt).toISOString(),
    audience: buildAudience(),
    audience_type: state.audienceType,
    template_params: {
      subject: state.subject,
      interval_min_seconds: state.minDelaySeconds,
      interval_max_seconds: state.maxDelaySeconds,
    },
  };

  emit('submit', campaignDetails);
};

// Cancel Form
const handleCancel = () => {
  emit('cancel');
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Campaign Title -->
    <div class="flex flex-col gap-1">
      <Input
        v-model="state.title"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.PLACEHOLDER')"
        :has-error="!!formErrors.title"
        :message="formErrors.title"
      />
    </div>

    <!-- Inbox Selector -->
    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <span v-if="formErrors.inbox" class="text-xs text-r-text">
        {{ formErrors.inbox }}
      </span>
    </div>

    <!-- Email Subject -->
    <div class="flex flex-col gap-1">
      <Input
        v-model="state.subject"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.PLACEHOLDER')"
        :has-error="!!formErrors.subject"
        :message="formErrors.subject"
      />
    </div>

    <!-- Message Content (HTML) -->
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.LABEL') }}
      </label>
      <TextArea
        v-model="state.messageContent"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.PLACEHOLDER')"
        rows="8"
      />
      <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.HINT') }}
        <code class="px-1 bg-n-alpha-2 rounded">{{ varPrimeiroNome }}</code
        >, <code class="px-1 bg-n-alpha-2 rounded">{{ varNome }}</code
        >, <code class="px-1 bg-n-alpha-2 rounded">{{ varEmail }}</code
        >, <code class="px-1 bg-n-alpha-2 rounded">{{ varTelefone }}</code
        >,
        <code class="px-1 bg-n-alpha-2 rounded">{{ varEmpresa }}</code>
      </p>
      <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
      <span v-if="formErrors.message" class="text-xs text-r-text">
        {{ formErrors.message }}
      </span>
    </div>

    <!-- Audience Type Selector -->
    <div class="flex flex-col gap-1">
      <label
        for="audienceType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE_TYPE.LABEL') }}
      </label>
      <ComboBox
        id="audienceType"
        v-model="state.audienceType"
        :options="audienceTypeOptions"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE_TYPE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Tags Audience -->
    <div v-if="state.audienceType === 'tags'" class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Contacts Audience -->
    <div v-if="state.audienceType === 'contacts'" class="flex flex-col gap-1">
      <label for="contacts" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.LABEL') }}
      </label>
      <ContactMultiSelectComboBox
        v-model="state.selectedContacts"
        :has-error="formErrors.audience !== ''"
        :message="
          state.selectedContacts.length === 0 && formErrors.audience
            ? t('CAMPAIGN.EMAIL.CREATE.FORM.CONTACTS.ERROR')
            : ''
        "
      />
    </div>

    <!-- Funnel/Stage Selector -->
    <div v-if="state.audienceType === 'funnel'" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <label for="funnel" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.FUNNEL.LABEL') }}
        </label>
        <ComboBox
          id="funnel"
          v-model="state.selectedFunnel"
          :options="funnelOptions"
          :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.FUNNEL.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>

      <div v-if="state.selectedFunnel" class="flex flex-col gap-1">
        <label for="stage" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.STAGE.LABEL') }}
        </label>
        <ComboBox
          id="stage"
          v-model="state.selectedStage"
          :options="stageOptions"
          :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.STAGE.PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.EMAIL.CREATE.FORM.STAGE.INFO') }}
        </p>
      </div>
    </div>

    <!-- Scheduled At -->
    <div class="flex flex-col gap-1">
      <label
        for="scheduledAt"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.LABEL') }}
      </label>
      <input
        id="scheduledAt"
        v-model="state.scheduledAt"
        type="datetime-local"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
        :min="new Date().toISOString().slice(0, 16)"
      />
      <span v-if="formErrors.scheduledAt" class="text-xs text-r-text">
        {{ formErrors.scheduledAt }}
      </span>
    </div>

    <!-- Advanced Settings Toggle -->
    <div class="flex items-center gap-2 pt-2">
      <Switch v-model="state.showAdvanced" />
      <span class="text-sm text-n-slate-11">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ADVANCED.TOGGLE') }}
      </span>
    </div>

    <!-- Advanced Settings -->
    <div
      v-if="state.showAdvanced"
      class="flex flex-col gap-4 p-4 border border-n-weak rounded-lg bg-n-alpha-1"
    >
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ADVANCED.TITLE') }}
      </h4>

      <!-- Delay Settings -->
      <div class="grid grid-cols-2 gap-4">
        <Input
          v-model.number="state.minDelaySeconds"
          type="number"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.ADVANCED.MIN_DELAY')"
          :min="1"
          :max="60"
        />
        <Input
          v-model.number="state.maxDelaySeconds"
          type="number"
          :label="t('CAMPAIGN.EMAIL.CREATE.FORM.ADVANCED.MAX_DELAY')"
          :min="1"
          :max="120"
        />
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.ADVANCED.DELAY_HINT') }}
      </p>
    </div>

    <!-- Form Actions -->
    <div
      class="flex items-center justify-end gap-2 pt-4 border-t border-n-weak"
    >
      <Button type="button" variant="faded" color="slate" @click="handleCancel">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CANCEL') }}
      </Button>
      <Button
        type="submit"
        :is-loading="isCreatingCampaign"
        :disabled="isCreatingCampaign"
      >
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CREATE') }}
      </Button>
    </div>
  </form>
</template>
